import * as React from "react"
import { Clock, History } from "lucide-react"
import { EmptyState } from "@ui/EmptyState"
import { ScrollArea } from "@ui/ScrollArea"
import { Select } from "@ui/Select"
import { cn } from "@lib/cn"
import type { RequestExampleSummary, RequestRun } from "@lib/inertia-types"
import { ResponsePane } from "./ResponsePane"

const STATUS_STYLES: Record<string, string> = {
  success: "text-cloverhound-green-600 dark:text-cloverhound-green-400",
  http_error: "text-cloverhound-orange-500 dark:text-cloverhound-orange-300",
  timeout: "text-cloverhound-yellow-600 dark:text-cloverhound-yellow-400",
  blocked: "text-cloverhound-red-500 dark:text-cloverhound-red-400",
  error: "text-cloverhound-red-500 dark:text-cloverhound-red-400",
}

const ALL = "__all__"
const SCRATCHPAD = "__scratchpad__"

export function HistoryTab({
  run,
  running,
  history,
  examples = [],
  activeExampleId = null,
  onSelectRun,
  onSaveAsMock,
  savingMock = false,
  onSaveAsExample,
  savingExample = false,
}: {
  run: RequestRun | null
  running: boolean
  history: RequestRun[]
  examples?: RequestExampleSummary[]
  activeExampleId?: number | null
  onSelectRun?: (run: RequestRun) => void
  onSaveAsMock?: (run: RequestRun) => void
  savingMock?: boolean
  onSaveAsExample?: (run: RequestRun) => void
  savingExample?: boolean
}) {
  // When an example is selected, its history defaults to just its own runs.
  const [filter, setFilter] = React.useState<string>(activeExampleId ? String(activeExampleId) : ALL)

  const exampleName = React.useMemo(() => new Map(examples.map((e) => [e.id, e.name])), [examples])
  const labelFor = React.useCallback(
    (h: RequestRun) => (h.request_example_id == null ? "Scratchpad" : exampleName.get(h.request_example_id) ?? "Example"),
    [exampleName],
  )

  const filtered = React.useMemo(
    () =>
      history.filter((h) => {
        if (filter === ALL) return true
        if (filter === SCRATCHPAD) return h.request_example_id == null
        return String(h.request_example_id) === filter
      }),
    [history, filter],
  )

  if (history.length === 0 && !run && !running) {
    return (
      <div className="grid h-full place-items-center">
        <EmptyState
          icon={<History className="size-6" aria-hidden="true" />}
          title="No runs yet"
          description="Send the request (⌘/Ctrl + Enter) to record a run here."
        />
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0">
      <div className="flex w-52 shrink-0 flex-col border-r border-cloverhound-gray-200 dark:border-cloverhound-gray-850">
        <div className="flex items-center justify-between gap-2 px-3 pb-1 pt-2">
          <span className="text-mini font-semibold uppercase tracking-wide text-cloverhound-gray-600">
            Recent runs
          </span>
        </div>
        <div className="px-2 pb-1.5">
          <Select.Root value={filter} onValueChange={(v) => v && setFilter(v)}>
            <Select.Trigger aria-label="Filter runs" className="h-6 text-mini">
              <Select.Value />
            </Select.Trigger>
            <Select.Content>
              <Select.Item value={ALL}>All runs</Select.Item>
              <Select.Item value={SCRATCHPAD}>Scratchpad</Select.Item>
              {examples.map((e) => (
                <Select.Item key={e.id} value={String(e.id)}>
                  {e.name}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        </div>
        <ScrollArea className="min-h-0 flex-1" viewportClassName="px-2 pb-2">
          {filtered.length === 0 ? (
            <p className="px-2 py-3 text-mini text-cloverhound-gray-600">No runs for this filter.</p>
          ) : (
            <div className="flex flex-col gap-0.5">
              {filtered.map((h) => {
                const selected = run?.id === h.id
                return (
                  <button
                    key={h.id}
                    type="button"
                    onClick={() => onSelectRun?.(h)}
                    className={cn(
                      "flex flex-col gap-0.5 rounded-md px-2 py-1.5 text-left text-mini transition-colors",
                      selected
                        ? "bg-cloverhound-gray-100 dark:bg-cloverhound-gray-850"
                        : "hover:bg-cloverhound-gray-100 dark:hover:bg-cloverhound-gray-850",
                    )}
                  >
                    <span className="flex items-center gap-2">
                      <span className={cn("font-mono text-mini font-semibold", STATUS_STYLES[h.status])}>
                        {h.response_status ?? h.status.toUpperCase()}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-cloverhound-gray-600">
                        {new Date(h.created_at).toLocaleTimeString()}
                      </span>
                      {h.duration_ms !== null && (
                        <span className="flex shrink-0 items-center gap-0.5 text-micro text-cloverhound-gray-500">
                          <Clock className="size-3" aria-hidden="true" />
                          {h.duration_ms}
                        </span>
                      )}
                    </span>
                    <span
                      className={cn(
                        "truncate text-micro",
                        h.request_example_id == null
                          ? "italic text-cloverhound-gray-500"
                          : "text-cloverhound-gray-600",
                      )}
                    >
                      {labelFor(h)}
                    </span>
                  </button>
                )
              })}
            </div>
          )}
        </ScrollArea>
      </div>

      <div className="min-h-0 flex-1">
        <ResponsePane
          run={run}
          running={running}
          history={[]}
          onSaveAsMock={onSaveAsMock}
          savingMock={savingMock}
          onSaveAsExample={onSaveAsExample}
          savingExample={savingExample}
        />
      </div>
    </div>
  )
}
